import React, { useEffect } from 'react';
import { Gift, ArrowRight, Bot, Zap, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SectionLayout from '@/components/SectionLayout';
import Footer from '@/components/Footer';
import CountdownTimer from '@/components/CountdownTimer';
import LaunchpadFinalCTA from '@/components/launchpad/LaunchpadFinalCTA';
import { useMetaTracking } from '@/hooks/useMetaTracking';

// Add type declaration for the NDRSL property on the Window interface
declare global {
  interface Window {
    NDRSL: {
      init: () => void;
    };
  }
}

const LaunchpadOffer = () => {
  const {
    trackViewContent,
    trackLead
  } = useMetaTracking();

  // Set target date to June 26th, 2025 at 12:00 AM EST for the Launch Special
  const launchSpecialTargetDate = new Date('2025-06-26T05:00:00.000Z'); // 12:00 AM EST = 5:00 AM UTC

  // Keep the original target date for the main timer
  const targetDate = new Date('2025-06-10T05:00:00.000Z'); // 12:00 AM EST = 5:00 AM UTC

  useEffect(() => {
    // Track page view
    trackViewContent({
      content_name: 'Launchpad Special Offer',
      content_category: 'special_offer_page'
    });

    // Create and load the Endorsal script
    const script = document.createElement('script');
    script.src = 'https://cdn.endorsal.io/widgets/widget.min.js';
    script.async = true;

    // Initialize the widget once the script loads
    script.onload = () => {
      if (window.NDRSL) {
        window.NDRSL.init();
      }
    };
    document.body.appendChild(script);

    // Clean up on unmount
    return () => {
      document.body.removeChild(script);

      // Remove any existing widgets from previous renders
      const existingWidgets = document.querySelectorAll('.ndrsl-widget-loaded');
      existingWidgets.forEach(widget => {
        if (widget.parentNode) {
          widget.parentNode.removeChild(widget);
        }
      });
    };
  }, [trackViewContent]);
  const handleCTAClick = async () => {
    // Track the lead conversion
    await trackLead({
      content_name: 'Launchpad Special Offer CTA'
    });

    // Open Stripe checkout
    window.open('https://buy.stripe.com/bIY17EaCh2FI0NicN5', '_blank');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#003a70]/5 to-white">
        {/* SEO - Meta tags to prevent indexing */}
        <head>
          <title>Special Launchpad Offer | AskSpot</title>
          <meta name="robots" content="noindex, nofollow" />
        </head>
        
        {/* Launch Special Countdown Banner */}
        <div className="bg-[#003a70] py-3 md:py-4 sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto px-4 md:px-8">
            <div className="text-center">
              <h3 className="text-sm md:text-lg font-bold text-white mb-2">Launch Special Offer Closes In:</h3>
              <CountdownTimer 
                targetDate={launchSpecialTargetDate} 
                className="bg-transparent text-white"
              />
            </div>
          </div>
        </div>
        
        {/* Original Countdown Timer */}
        
        <main className="flex-grow">
          <SectionLayout paddingY="py-8 sm:py-12 md:py-16 lg:py-20" mobilePaddingY="py-6" backgroundColor="bg-transparent" containerWidth="medium" decorativeElements={<>
                {/* Decorative elements */}
                <div className="absolute top-[20%] left-[10%] animate-float opacity-30 hidden lg:block">
                  <Gift className="text-[#003a70] h-8 w-8" />
                </div>
                
                <div className="absolute top-[40%] right-[15%] animate-float opacity-25 hidden lg:block" style={{
            animationDelay: '1.5s'
          }}>
                  <Gift className="text-[#FEE6D8] h-6 w-6" />
                </div>
                
                {/* Gradient orbs */}
                <div className="absolute top-[30%] right-[25%] w-32 h-32 bg-gradient-to-br from-[#003a70]/20 to-[#FEE6D8]/20 rounded-full blur-2xl animate-pulse opacity-60 hidden lg:block"></div>
                <div className="absolute bottom-[25%] left-[20%] w-40 h-40 bg-gradient-to-br from-[#FEE6D8]/15 to-[#003a70]/15 rounded-full blur-xl animate-float opacity-50 hidden lg:block" style={{
            animationDelay: '2s'
          }}></div>
              </>}>
            <div className="max-w-4xl mx-auto text-center px-3 sm:px-4">
              {/* Enhanced Headline */}
              <div className="relative mb-6 sm:mb-8 md:mb-10 lg:mb-12">
                {/* Background glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#003a70]/5 via-[#FEE6D8]/10 to-[#003a70]/5 blur-3xl rounded-3xl transform scale-110"></div>
                
                <h1 className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-5xl font-black leading-[1.1] tracking-tight">
                  <span className="block mb-2 sm:mb-3">
                    <span className="bg-gradient-to-r from-[#003a70] via-[#004a8a] to-[#003a70] bg-clip-text text-transparent text-4xl">Ready To Start Building Ai Powered Business Cards?</span>
                  </span>
                  <span className="block mb-2 sm:mb-3">
                    
                  </span>
                  <span className="block mb-3 sm:mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-4xl font-bold text-gray-700 leading-tight">🔥 Try AskSpot Unlimited</span>
                  <span className="block mb-2 sm:mb-3">
                    <span className="relative inline-block">
                      <span className="bg-gradient-to-r from-[#003a70] to-[#004a8a] bg-clip-text text-transparent font-black">
                        100% RISK-FREE
                      </span>
                      <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-[#FEE6D8] to-[#003a70]/20 rounded-full"></div>
                    </span>
                  </span>
                  <span className="block mb-2 sm:mb-3 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-4xl font-bold text-gray-700">
                    for 60 Days &amp;
                  </span>
                  <span className="block">
                    <span className="bg-gradient-to-r from-[#003a70] via-[#004a8a] to-[#003a70] bg-clip-text text-transparent">
                      Lock In HUGE Savings!
                    </span>
                  </span>
                </h1>
                
                {/* Accent elements */}
                <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-[#FEE6D8] to-[#003a70]/20 rounded-full opacity-60 animate-pulse hidden sm:block"></div>
                <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-[#003a70]/20 to-[#FEE6D8] rounded-full opacity-40 animate-float hidden sm:block" style={{
                animationDelay: '1s'
              }}></div>
              </div>
              
              {/* Enhanced Magic Cards Design */}
              <div className="mb-8 sm:mb-10 md:mb-12 lg:mb-16">
                <div className="max-w-5xl mx-auto">
                  {/* Magical Value Proposition Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                    {/* Card 1 - Build Unlimited */}
                    <div className="group relative bg-gradient-to-br from-white via-[#FEE6D8]/5 to-[#003a70]/5 backdrop-blur-sm border-2 border-[#003a70]/20 rounded-2xl p-4 sm:p-6 shadow-xl hover:shadow-3xl transition-all duration-700 hover:border-[#003a70]/40 hover:scale-110 animate-fade-in overflow-hidden transform-gpu">
                      {/* Magical glow background */}
                      <div className="absolute -inset-2 bg-gradient-to-br from-[#003a70]/10 via-[#FEE6D8]/20 to-[#003a70]/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-110"></div>
                      
                      {/* Animated particle effects */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                        <div className="absolute top-2 left-2 w-1.5 h-1.5 bg-[#003a70]/60 rounded-full animate-ping" style={{
                        animationDelay: '0.2s'
                      }}></div>
                        <div className="absolute top-4 right-4 w-1 h-1 bg-[#FEE6D8]/80 rounded-full animate-ping" style={{
                        animationDelay: '0.8s'
                      }}></div>
                        <div className="absolute bottom-3 left-6 w-2 h-2 bg-[#003a70]/40 rounded-full animate-ping" style={{
                        animationDelay: '1.4s'
                      }}></div>
                        <div className="absolute top-8 left-1/2 w-1 h-1 bg-[#FEE6D8]/60 rounded-full animate-ping" style={{
                        animationDelay: '2s'
                      }}></div>
                        <div className="absolute bottom-6 right-3 w-1.5 h-1.5 bg-[#003a70]/50 rounded-full animate-ping" style={{
                        animationDelay: '2.6s'
                      }}></div>
                      </div>
                      
                      {/* Gradient border animation */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#003a70]/0 via-[#FEE6D8]/20 to-[#003a70]/0 opacity-0 group-hover:opacity-100 animate-pulse transition-opacity duration-700"></div>
                      
                      <div className="relative z-10">
                        <div className="flex items-center justify-center mb-3 sm:mb-4 transform group-hover:scale-110 transition-transform duration-500">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#003a70] via-[#004a8a] to-[#002a60] rounded-xl flex items-center justify-center shadow-2xl group-hover:shadow-3xl group-hover:shadow-[#003a70]/25 transition-all duration-500 relative overflow-hidden">
                            {/* Icon glow effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-[#003a70]/20 to-[#FEE6D8]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
                            <Bot className="text-white w-7 h-7 sm:w-8 sm:h-8 relative z-10 transition-all duration-500 group-hover:animate-pulse group-hover:scale-110" />
                            {/* Sparkle effect */}
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#FEE6D8] rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                          </div>
                        </div>
                        <h3 className="text-[#003a70] font-bold text-lg sm:text-xl mb-2 group-hover:text-[#002a60] transition-all duration-500 group-hover:scale-105 transform">Build Unlimited</h3>
                        <p className="text-gray-600 text-sm sm:text-base group-hover:text-gray-800 transition-all duration-500 group-hover:scale-105 transform">AI Assistants</p>
                      </div>
                    </div>

                    {/* Card 2 - Access All */}
                    <div className="group relative bg-gradient-to-br from-white via-[#FEE6D8]/5 to-[#003a70]/5 backdrop-blur-sm border-2 border-[#003a70]/20 rounded-2xl p-4 sm:p-6 shadow-xl hover:shadow-3xl transition-all duration-700 hover:border-[#003a70]/40 hover:scale-110 animate-fade-in overflow-hidden transform-gpu" style={{
                    animationDelay: '0.2s'
                  }}>
                      {/* Magical glow background */}
                      <div className="absolute -inset-2 bg-gradient-to-br from-[#FEE6D8]/15 via-[#003a70]/10 to-[#FEE6D8]/15 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-110"></div>
                      
                      {/* Animated particle effects */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                        <div className="absolute top-3 left-4 w-1 h-1 bg-[#FEE6D8]/80 rounded-full animate-ping" style={{
                        animationDelay: '0.4s'
                      }}></div>
                        <div className="absolute top-6 right-2 w-1.5 h-1.5 bg-[#003a70]/60 rounded-full animate-ping" style={{
                        animationDelay: '1s'
                      }}></div>
                        <div className="absolute bottom-4 right-5 w-2 h-2 bg-[#FEE6D8]/50 rounded-full animate-ping" style={{
                        animationDelay: '1.6s'
                      }}></div>
                        <div className="absolute top-1/2 left-2 w-1 h-1 bg-[#003a70]/70 rounded-full animate-ping" style={{
                        animationDelay: '2.2s'
                      }}></div>
                        <div className="absolute bottom-8 left-1/3 w-1.5 h-1.5 bg-[#FEE6D8]/40 rounded-full animate-ping" style={{
                        animationDelay: '2.8s'
                      }}></div>
                      </div>
                      
                      {/* Gradient border animation */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#FEE6D8]/0 via-[#003a70]/20 to-[#FEE6D8]/0 opacity-0 group-hover:opacity-100 animate-pulse transition-opacity duration-700"></div>
                      
                      <div className="relative z-10">
                        <div className="flex items-center justify-center mb-3 sm:mb-4 transform group-hover:scale-110 transition-transform duration-500">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#003a70] via-[#004a8a] to-[#002a60] rounded-xl flex items-center justify-center shadow-2xl group-hover:shadow-3xl group-hover:shadow-[#003a70]/25 transition-all duration-500 relative overflow-hidden">
                            {/* Icon glow effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-[#FEE6D8]/20 to-[#003a70]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
                            <Zap className="text-white w-7 h-7 sm:w-8 sm:h-8 relative z-10 transition-all duration-500 group-hover:animate-pulse group-hover:scale-110" />
                            {/* Sparkle effect */}
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#FEE6D8] rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                          </div>
                        </div>
                        <h3 className="text-[#003a70] font-bold text-lg sm:text-xl mb-2 group-hover:text-[#002a60] transition-all duration-500 group-hover:scale-105 transform">Access All</h3>
                        <p className="text-gray-600 text-sm sm:text-base group-hover:text-gray-800 transition-all duration-500 group-hover:scale-105 transform">Features</p>
                      </div>
                    </div>

                    {/* Card 3 - See the Power */}
                    <div className="group relative bg-gradient-to-br from-white via-[#FEE6D8]/5 to-[#003a70]/5 backdrop-blur-sm border-2 border-[#003a70]/20 rounded-2xl p-4 sm:p-6 shadow-xl hover:shadow-3xl transition-all duration-700 hover:border-[#003a70]/40 hover:scale-110 animate-fade-in overflow-hidden transform-gpu" style={{
                    animationDelay: '0.4s'
                  }}>
                      {/* Magical glow background */}
                      <div className="absolute -inset-2 bg-gradient-to-br from-[#003a70]/10 via-[#FEE6D8]/15 to-[#003a70]/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-110"></div>
                      
                      {/* Animated particle effects */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                        <div className="absolute top-5 left-3 w-1.5 h-1.5 bg-[#003a70]/70 rounded-full animate-ping" style={{
                        animationDelay: '0.6s'
                      }}></div>
                        <div className="absolute top-2 right-6 w-1 h-1 bg-[#FEE6D8]/60 rounded-full animate-ping" style={{
                        animationDelay: '1.2s'
                      }}></div>
                        <div className="absolute bottom-2 left-5 w-2 h-2 bg-[#003a70]/50 rounded-full animate-ping" style={{
                        animationDelay: '1.8s'
                      }}></div>
                        <div className="absolute top-1/3 right-2 w-1 h-1 bg-[#FEE6D8]/80 rounded-full animate-ping" style={{
                        animationDelay: '2.4s'
                      }}></div>
                        <div className="absolute bottom-6 right-1/4 w-1.5 h-1.5 bg-[#003a70]/60 rounded-full animate-ping" style={{
                        animationDelay: '3s'
                      }}></div>
                      </div>
                      
                      {/* Gradient border animation */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#003a70]/0 via-[#FEE6D8]/25 to-[#003a70]/0 opacity-0 group-hover:opacity-100 animate-pulse transition-opacity duration-700"></div>
                      
                      <div className="relative z-10">
                        <div className="flex items-center justify-center mb-3 sm:mb-4 transform group-hover:scale-110 transition-transform duration-500">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#003a70] via-[#004a8a] to-[#002a60] rounded-xl flex items-center justify-center shadow-2xl group-hover:shadow-3xl group-hover:shadow-[#003a70]/25 transition-all duration-500 relative overflow-hidden">
                            {/* Icon glow effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-[#003a70]/20 to-[#FEE6D8]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
                            <Eye className="text-white w-7 h-7 sm:w-8 sm:h-8 relative z-10 transition-all duration-500 group-hover:animate-pulse group-hover:scale-110" />
                            {/* Sparkle effect */}
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#FEE6D8] rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                          </div>
                        </div>
                        <h3 className="text-[#003a70] font-bold text-lg sm:text-xl mb-2 group-hover:text-[#002a60] transition-all duration-500 group-hover:scale-105 transform">See the Power</h3>
                        <p className="text-gray-600 text-sm sm:text-base group-hover:text-gray-800 transition-all duration-500 group-hover:scale-105 transform">for Yourself</p>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Highlight */}
                  <div className="bg-gradient-to-r from-[#FEE6D8]/30 via-white/50 to-[#FEE6D8]/30 backdrop-blur-sm border-2 border-[#003a70]/20 rounded-2xl p-4 sm:p-5 md:p-6 shadow-xl">
                    <div className="text-center">
                      <div className="mb-3 sm:mb-4">
                        <span className="text-xl sm:text-2xl md:text-3xl text-gray-700 font-medium">Start with </span> 
                        <span className="relative inline-block">
                          <span className="bg-gradient-to-r from-[#003a70] to-[#004a8a] bg-clip-text text-transparent font-black text-3xl sm:text-4xl md:text-5xl">
                            ZERO COST
                          </span>
                          <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-[#FEE6D8] to-[#003a70]/30 rounded-full"></div>
                        </span>
                        <span className="block text-lg sm:text-xl md:text-2xl text-gray-600 mt-2">for Two Full Months</span>
                      </div>
                      
                      <div className="border-t border-[#003a70]/10 pt-3 sm:pt-4">
                        <span className="text-lg sm:text-xl md:text-2xl text-gray-700">Then continue for just </span> 
                        <span className="relative inline-block">
                          <span className="bg-gradient-to-r from-[#003a70] to-[#004a8a] bg-clip-text text-transparent font-bold text-2xl sm:text-3xl md:text-4xl">
                            $59/mo
                          </span>
                          <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FEE6D8] to-[#003a70]/30 rounded-full"></div>
                        </span>
                        <span className="text-gray-500 line-through ml-3 text-lg sm:text-xl md:text-2xl">(Normally $99/mo!)</span>
                      </div>
                      
                      <div className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-[#003a70]/10">
                        <p className="text-base sm:text-lg md:text-xl text-gray-600 font-medium">
                          🎥 Dave Explains Everything in the Video Below!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Video Section - Real Loom Embed */}
              <div className="w-full max-w-4xl mx-auto mb-8 sm:mb-10 md:mb-12 lg:mb-16">
                <div className="relative rounded-xl overflow-hidden shadow-2xl border-2 border-[#003a70]/20">
                  <div className="w-full h-full" dangerouslySetInnerHTML={{
                  __html: `<div style="position: relative; padding-bottom: 64.13301662707838%; height: 0;"><iframe src="https://www.loom.com/embed/1c17e980aab94c9c899b5c00ee9290bd?sid=db6546e4-7b1f-49cb-8d3a-1e43e2538924&hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`
                }} />
                </div>
              </div>

              {/* Bold CTA Button */}
              <div className="flex justify-center mb-4">
                <Button size="lg" className="bg-gradient-to-r from-[#003a70] to-[#004a8a] hover:from-[#002a60] hover:to-[#003a70] text-white text-lg sm:text-xl md:text-2xl font-bold py-6 sm:py-7 md:py-8 px-8 sm:px-10 md:px-12 shadow-2xl hover:shadow-3xl group transform hover:scale-105 transition-all duration-300 rounded-2xl border-2 border-[#003a70]/20 min-h-[60px] sm:min-h-[70px] md:min-h-[80px]" onClick={handleCTAClick}>
                  <Gift className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 mr-3 sm:mr-4 flex-shrink-0" />
                  <span className="flex-1">Claim Your Special Offer</span>
                  <ArrowRight className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 ml-3 sm:ml-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Button>
              </div>

              {/* Warning Text */}
              <div className="mb-8 sm:mb-10 md:mb-12 lg:mb-16">
                <p className="text-lg sm:text-xl text-[#003a70] font-medium">
                  ⚠️ This Offer Is For New Users Only
                </p>
              </div>

              {/* Testimonials Section */}
              <div className="w-full max-w-6xl mx-auto">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#003a70] mb-6 sm:mb-8">
                  See What Our Users Are Saying
                </h2>
                
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 md:p-8 px-0">
                  <div id='ndrsl-wol-65fb2b1f2861c0430c92b45a' className='ndrsl-widget'></div>
                </div>
              </div>
            </div>
          </SectionLayout>
        </main>

        {/* New Final CTA Section */}
        <LaunchpadFinalCTA />

        <Footer />
    </div>
  );
};

export default LaunchpadOffer;
